import { ApiError, errorMessage } from "@esp/api-client";
import { Alert, Button, PageLoader } from "@esp/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, type ReactNode } from "react";

import { admin, auth, session, unwrap } from "../app/api";
import { LoginPage } from "./LoginPage";
import { StaffContext, STAFF_QUERY_KEY, type Staff } from "./staff";

async function fetchStaff(): Promise<Staff> {
  const me = await unwrap(admin.GET("/admin/v1/me"));
  session.setCsrfToken(me.csrf_token);
  return me;
}

function Blocked({ title, children, onSignOut }: { title: string; children: ReactNode; onSignOut: () => void }) {
  return (
    <div className="mx-auto mt-24 max-w-md space-y-4 p-6">
      <Alert tone="error" title={title}>{children}</Alert>
      <Button variant="secondary" onClick={onSignOut}>Sign out</Button>
    </div>
  );
}

/**
 * Gate for the whole admin app: only signed-in platform staff with MFA get past it.
 * Everything else is explained, never partially rendered.
 */
export function StaffProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const me = useQuery({ queryKey: STAFF_QUERY_KEY, queryFn: fetchStaff, retry: false, staleTime: 5 * 60_000 });

  const forget = useCallback(() => {
    session.setCsrfToken(null);
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== STAFF_QUERY_KEY[0] });
    void queryClient.invalidateQueries({ queryKey: STAFF_QUERY_KEY });
  }, [queryClient]);
  useEffect(() => session.onUnauthorized(forget), [forget]);

  const signOut = useCallback(async () => {
    try {
      await unwrap(auth.POST("/api/v1/auth/logout"));
    } catch {
      // The session may already be gone; the local state is cleared either way.
    }
    forget();
  }, [forget]);
  const value = useMemo(() => (me.data ? { staff: me.data, signOut } : null), [me.data, signOut]);

  if (me.isPending) return <PageLoader />;
  if (me.error instanceof ApiError) {
    if (me.error.status === 401) {
      return <LoginPage notice={me.error.code === "staff_session_expired" ? me.error.detail : undefined} />;
    }
    if (me.error.status === 404) {
      return <Blocked title="This account is not platform staff" onSignOut={signOut}>Customer accounts use the dashboard, not this panel.</Blocked>;
    }
    return <Blocked title="Access denied" onSignOut={signOut}>{errorMessage(me.error)}</Blocked>;
  }
  if (!value) return <Blocked title="The admin API is unavailable" onSignOut={signOut}>{errorMessage(me.error)}</Blocked>;
  return <StaffContext.Provider value={value}>{children}</StaffContext.Provider>;
}
